<template>
  <div class="base-combobox__group">
    <!-- The node's own row: a group (has children) shows a tri-state checkbox
         and an expand toggle; a leaf shows a plain checkbox. Indented by depth. -->
    <div
      :id="rowId"
      class="base-combobox__row"
      :class="{
        'base-combobox__row--parent': isGroup,
        'base-combobox__row--child': !isGroup,
        'is-highlighted': combobox.activeId === rowId,
      }"
      :style="{ '--cbx-depth': depth }"
      @mouseenter="combobox.setActive(rowId)"
    >
      <FormCheckbox
        class="base-combobox__row-check"
        size="small"
        :value="state.selected"
        :indeterminate="state.indeterminate"
        :label="node.label"
        @input="toggle"
      />
      <button
        v-if="isGroup && !combobox.isSearching"
        type="button"
        class="base-combobox__expand"
        :aria-label="node.label"
        :aria-expanded="String(expanded)"
        @click="combobox.toggleExpand(node)"
      >
        <BaseIcon :name="expanded ? 'chevron-up' : 'chevron-down'" />
      </button>
    </div>

    <!-- Children render through this same component, one level deeper. -->
    <div v-if="isGroup && expanded" class="base-combobox__children">
      <BaseComboBoxNode v-for="child in node.children" :key="child.value" :node="child" :depth="depth + 1" />
    </div>
  </div>
</template>

<script>
import BaseIcon from '@/components/base/BaseIcon';
import FormCheckbox from '@/components/form/FormCheckbox';

/**
 * One node of BaseComboBox's option tree, rendered recursively so the tree can
 * be any depth (e.g. province -> région -> ville). Selection, expansion and
 * keyboard highlight all live on the owning BaseComboBox, reached through
 * `inject` — this component only renders and forwards.
 */
export default {
  name: 'BaseComboBoxNode',
  components: { BaseIcon, FormCheckbox },
  inject: ['combobox'],
  props: {
    // { value, label, children?: [node] } — a node without children is a leaf.
    node: {
      type: Object,
      required: true,
    },
    // 0 for a top-level node; drives the row indentation.
    depth: {
      type: Number,
      default: 0,
    },
  },
  computed: {
    isGroup() {
      return this.combobox.hasChildren(this.node);
    },
    rowId() {
      return this.combobox.itemId(this.isGroup ? 'group' : 'child', this.node);
    },
    state() {
      return this.combobox.groupState(this.node);
    },
    expanded() {
      return this.combobox.isExpanded(this.node);
    },
  },
  methods: {
    toggle() {
      if (this.isGroup) this.combobox.toggleGroup(this.node);
      else this.combobox.toggleChild(this.node);
    },
  },
};
</script>

<style lang="scss" scoped>
// Shorthand for the nested $aloha-sizes lookup.
@function aloha-size($size, $key) {
  @return map-get(map-get($aloha-sizes, $size), $key);
}

// Each nesting level indents the row past its parent's checkbox so the
// hierarchy reads at a glance (depth 1 lands where the classic child row did).
$cbx-indent-step: 28px;

.base-combobox {
  &__row {
    display: flex;
    align-items: center;
    padding: aloha-size('xs', 'py') aloha-size('md', 'px');
    padding-left: calc(#{aloha-size('md', 'px')} + var(--cbx-depth, 0) * #{$cbx-indent-step});

    // Mouse hover and keyboard highlight share one resting state.
    &:hover,
    &.is-highlighted {
      background-color: $color-hover;
    }
  }

  &__row--parent {
    justify-content: space-between;
    font-weight: $font-weight-bold;
  }

  &__row-check {
    flex: 1 1 auto;
    min-width: 0;
  }

  &__expand {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex: 0 0 auto;
    // Padding + matching negative margin enlarges the tap target to a
    // comfortable size without growing the row height.
    padding: $space-2;
    margin: -$space-2 (-$space-1) -$space-2 0;
    border: none;
    background: transparent;
    color: $color-text-muted;
    cursor: pointer;

    &:hover {
      color: $color-text-body;
    }
  }
}
</style>
